// Physical Training (PT) sound engine.
//
// The sample PT sequences are drum / percussion cadences (march beats, running
// cadences, whistle signals, etc.). Rather than shipping audio assets, every
// hit is synthesised on the fly with the WebAudio API — the same approach the
// PTPlayer already uses for its transition beeps. This keeps the feature zero
// dependency and lets the step-sequencer trigger crisp, low-latency sounds.

let sharedCtx = null;
let noiseBuffer = null;

// Lazily create (and resume) a single shared AudioContext. Browsers require the
// context to be created / resumed from a user gesture, so callers invoke this
// off a click (play button).
export function getAudioContext() {
    if (typeof window === "undefined") return null;
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return null;
    if (!sharedCtx) sharedCtx = new Ctx();
    if (sharedCtx.state === "suspended") sharedCtx.resume().catch(() => {});
    return sharedCtx;
}

// One second of white noise, reused by the noise-based instruments (snare,
// hi-hat, clap, cymbal).
function getNoiseBuffer(ctx) {
    if (noiseBuffer) return noiseBuffer;
    const size = ctx.sampleRate;
    const buffer = ctx.createBuffer(1, size, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < size; i += 1) data[i] = Math.random() * 2 - 1;
    noiseBuffer = buffer;
    return buffer;
}

/* --------------------------- instrument voices --------------------------- */

function kick(ctx, t, gain) {
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.connect(g);
    g.connect(ctx.destination);
    osc.frequency.setValueAtTime(150, t);
    osc.frequency.exponentialRampToValueAtTime(45, t + 0.12);
    g.gain.setValueAtTime(gain, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.28);
    osc.start(t);
    osc.stop(t + 0.3);
}

function snare(ctx, t, gain) {
    const noise = ctx.createBufferSource();
    noise.buffer = getNoiseBuffer(ctx);
    const hp = ctx.createBiquadFilter();
    hp.type = "highpass";
    hp.frequency.value = 1500;
    const ng = ctx.createGain();
    noise.connect(hp);
    hp.connect(ng);
    ng.connect(ctx.destination);
    ng.gain.setValueAtTime(gain, t);
    ng.gain.exponentialRampToValueAtTime(0.001, t + 0.2);
    noise.start(t);
    noise.stop(t + 0.2);

    const osc = ctx.createOscillator();
    osc.type = "triangle";
    const og = ctx.createGain();
    osc.connect(og);
    og.connect(ctx.destination);
    osc.frequency.setValueAtTime(180, t);
    og.gain.setValueAtTime(gain * 0.7, t);
    og.gain.exponentialRampToValueAtTime(0.001, t + 0.1);
    osc.start(t);
    osc.stop(t + 0.1);
}

function hihat(ctx, t, gain) {
    const noise = ctx.createBufferSource();
    noise.buffer = getNoiseBuffer(ctx);
    const hp = ctx.createBiquadFilter();
    hp.type = "highpass";
    hp.frequency.value = 7000;
    const g = ctx.createGain();
    noise.connect(hp);
    hp.connect(g);
    g.connect(ctx.destination);
    g.gain.setValueAtTime(gain * 0.6, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.05);
    noise.start(t);
    noise.stop(t + 0.05);
}

function tom(ctx, t, gain) {
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.connect(g);
    g.connect(ctx.destination);
    osc.frequency.setValueAtTime(220, t);
    osc.frequency.exponentialRampToValueAtTime(90, t + 0.18);
    g.gain.setValueAtTime(gain, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.3);
    osc.start(t);
    osc.stop(t + 0.32);
}

function woodblock(ctx, t, gain) {
    const osc = ctx.createOscillator();
    osc.type = "square";
    const g = ctx.createGain();
    osc.connect(g);
    g.connect(ctx.destination);
    osc.frequency.setValueAtTime(1200, t);
    g.gain.setValueAtTime(gain * 0.5, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.05);
    osc.start(t);
    osc.stop(t + 0.06);
}

function clap(ctx, t, gain) {
    for (let i = 0; i < 3; i += 1) {
        const noise = ctx.createBufferSource();
        noise.buffer = getNoiseBuffer(ctx);
        const bp = ctx.createBiquadFilter();
        bp.type = "bandpass";
        bp.frequency.value = 1200;
        const g = ctx.createGain();
        noise.connect(bp);
        bp.connect(g);
        g.connect(ctx.destination);
        const st = t + i * 0.02;
        g.gain.setValueAtTime(gain * 0.7, st);
        g.gain.exponentialRampToValueAtTime(0.001, st + 0.1);
        noise.start(st);
        noise.stop(st + 0.1);
    }
}

function whistle(ctx, t, gain) {
    const osc = ctx.createOscillator();
    osc.type = "sine";
    const g = ctx.createGain();
    const lfo = ctx.createOscillator();
    const lfoGain = ctx.createGain();
    lfo.frequency.value = 6;
    lfoGain.gain.value = 40;
    lfo.connect(lfoGain);
    lfoGain.connect(osc.frequency);
    osc.connect(g);
    g.connect(ctx.destination);
    osc.frequency.setValueAtTime(2100, t);
    g.gain.setValueAtTime(0.001, t);
    g.gain.exponentialRampToValueAtTime(gain * 0.5, t + 0.03);
    g.gain.setValueAtTime(gain * 0.5, t + 0.22);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.3);
    osc.start(t);
    lfo.start(t);
    osc.stop(t + 0.32);
    lfo.stop(t + 0.32);
}

function cymbal(ctx, t, gain) {
    const noise = ctx.createBufferSource();
    noise.buffer = getNoiseBuffer(ctx);
    const hp = ctx.createBiquadFilter();
    hp.type = "highpass";
    hp.frequency.value = 5000;
    const g = ctx.createGain();
    noise.connect(hp);
    hp.connect(g);
    g.connect(ctx.destination);
    g.gain.setValueAtTime(gain * 0.5, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.6);
    noise.start(t);
    noise.stop(t + 0.6);
}

/* ------------------------------ public API ------------------------------ */

// Ordered metadata for every voice the sequencer can trigger. `color` maps to a
// Chakra colour scheme so the step grid can tint each row.
export const INSTRUMENTS = {
    kick: {label: "Bass Drum", color: "red", play: kick},
    snare: {label: "Snare", color: "orange", play: snare},
    tom: {label: "Tom", color: "purple", play: tom},
    hihat: {label: "Hi-Hat", color: "yellow", play: hihat},
    wood: {label: "Wood Block", color: "teal", play: woodblock},
    clap: {label: "Clap", color: "pink", play: clap},
    whistle: {label: "Whistle", color: "blue", play: whistle},
    cymbal: {label: "Cymbal", color: "cyan", play: cymbal},
};

// Fire a single hit now. `accent` gives the downbeat a louder, punchier sound.
export function playHit(instrument, {accent = false} = {}) {
    const ctx = getAudioContext();
    if (!ctx) return;
    const def = INSTRUMENTS[instrument];
    if (!def) return;
    try {
        def.play(ctx, ctx.currentTime, accent ? 1 : 0.6);
    } catch (e) {
        /* audio unavailable — ignore */
    }
}
